/**
 * Coordinates authentication refresh with React Query refetches
 * Prevents race conditions and ensures queries use fresh tokens
 *
 * This module solves the tab-switching loading issue where queries fail
 * with expired tokens after returning from background tabs. It ensures
 * auth refresh and query refetch happen atomically.
 */

import { queryClient } from '@/lib/query-client';
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/lib/logger';
import { ensureFreshAuth } from '@/lib/auth-readiness';

export interface RefreshOptions {
  /**
   * Whether to refetch active queries after auth refresh
   * @default true
   */
  refetchActiveQueries?: boolean;

  /**
   * Specific query keys to invalidate (forces refetch)
   * @default []
   */
  invalidateQueryKeys?: string[][];

  /**
   * Minimum milliseconds between refresh attempts (debounce)
   * @default 2000
   */
  debounceMs?: number;
}

export interface RefreshResult {
  success: boolean;
  error?: string;
  tokenRefreshed?: boolean;
  queriesRefetched?: boolean;
}

let lastRefreshTimestamp = Date.now();
let inFlightPromise: Promise<RefreshResult> | null = null;
const EXPIRY_GRACE_SECONDS = 60; // refresh only if token expires within this window

/**
 * Coordinates Supabase session refresh with React Query refetching
 *
 * This function:
 * 1. Refreshes the Supabase auth session (gets new JWT token)
 * 2. Invalidates specified query keys
 * 3. Refetches active auth-dependent queries
 * 4. Debounces rapid refresh attempts to prevent abuse
 *
 * @param options - Configuration for refresh behavior
 * @returns Result object indicating success/failure
 *
 * @example
 * // On visibility change (tab refocus)
 * const result = await coordinatedAuthRefresh({
 *   refetchActiveQueries: true,
 *   debounceMs: 2000
 * });
 */
export async function coordinatedAuthRefresh(
  options: RefreshOptions = {}
): Promise<RefreshResult> {
  const {
    refetchActiveQueries = true,
    invalidateQueryKeys = [],
    debounceMs = 2000
  } = options;

  // Coalesce concurrent calls: if a refresh is in flight, await it
  if (inFlightPromise) {
    logger.debug('coordinatedAuthRefresh: Coalescing with in-flight refresh');
    return inFlightPromise;
  }

  const now = Date.now();
  inFlightPromise = (async (): Promise<RefreshResult> => {
    try {
      logger.info('coordinatedAuthRefresh: Starting', {
        refetchActiveQueries,
        invalidateQueryKeysCount: invalidateQueryKeys.length
      });

      // Step 0: Only refresh if token is near expiry
      let tokenRefreshed = false;
      let sessionExpiresAt: number | undefined;
      try {
        const { data: { session } } = await supabase.auth.getSession();
        sessionExpiresAt = session?.expires_at;
      } catch (e) {
        // proceed with refresh attempt if session cannot be read
      }

      const secondsUntilExpiry = sessionExpiresAt
        ? Math.floor(sessionExpiresAt - Date.now() / 1000)
        : undefined;

      const shouldRefresh = typeof secondsUntilExpiry === 'number' && secondsUntilExpiry <= EXPIRY_GRACE_SECONDS;

      if (shouldRefresh) {
        const readiness = await ensureFreshAuth({ source: 'authQueryCoordinator' });
        if (!readiness.ok) {
          logger.error('coordinatedAuthRefresh: ensureFreshAuth failed', {
            ok: readiness.ok,
            rotated: readiness.rotated,
            reason: readiness.reason,
            attempts: readiness.attempts,
            source: readiness.source
          });
          return {
            success: false,
            error: readiness.reason,
            tokenRefreshed: false,
          };
        }
        tokenRefreshed = readiness.rotated;
        logger.info('coordinatedAuthRefresh: ensureFreshAuth succeeded', {
          ok: readiness.ok,
          rotated: readiness.rotated,
          reason: readiness.reason,
          attempts: readiness.attempts,
          source: readiness.source
        });
      } else {
        logger.debug('coordinatedAuthRefresh: Skipping refresh (token not near expiry)', {
          secondsUntilExpiry
        });
      }

      lastRefreshTimestamp = now;

      // Step 2: Invalidate specific query keys (if provided)
      if (invalidateQueryKeys.length > 0) {
        for (const queryKey of invalidateQueryKeys) {
          await queryClient.invalidateQueries({ queryKey });
          logger.debug('coordinatedAuthRefresh: Invalidated query', { queryKey });
        }
      }

      // Step 3: Refetch active queries that depend on authentication (optional)
      let queriesRefetched = false;
      if (refetchActiveQueries) {
        await queryClient.refetchQueries({
          type: 'active',
          predicate: (query) => {
            const key = query.queryKey[0];
            const authDependentPrefixes = [
              'ethics-flags',
              'supervision-data',
              'verify-access',
              'subscription',
              'user',
              'dashboard',
              'sessions',
              'threads',
              'personas',
              'mentor',
              'coach-progress',
              'technique-analysis'
            ];
            const isAuthDependent = typeof key === 'string' && authDependentPrefixes.some(prefix => key.startsWith(prefix));
            if (isAuthDependent) {
              logger.debug('coordinatedAuthRefresh: Will refetch query', { queryKey: query.queryKey });
            }
            return isAuthDependent;
          }
        });
        queriesRefetched = true;
        logger.info('coordinatedAuthRefresh: Refetched active auth-dependent queries');
      }

      return {
        success: true,
        tokenRefreshed,
        queriesRefetched
      };

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      logger.error('coordinatedAuthRefresh: Failed', { error: errorMessage });
      return {
        success: false,
        error: errorMessage,
        tokenRefreshed: false,
        queriesRefetched: false
      };
    } finally {
      inFlightPromise = null;
    }
  })();

  return inFlightPromise;
}

/**
 * Get time since last auth refresh (for debugging/monitoring)
 */
export function getTimeSinceLastRefresh(): number {
  return Date.now() - lastRefreshTimestamp;
}

/**
 * Reset the refresh timestamp (for testing purposes)
 * @internal
 */
export function resetRefreshTimestamp(): void {
  lastRefreshTimestamp = 0;
}
